import { useEffect, useMemo, useState } from "react";
import { Button } from "primereact/button";
import { useDispatch } from "react-redux";
import useResizeListener from "../../../../hooks/useResizeListener";
import { Bed } from "../../../../interface/interface";
import { deleteBed, loadAllEstablishments } from "../../../../servicesHooks/Location/useLocationConfigService";
import { useLocationConfigContext } from "../../context/LocationConfigContext";
import { ETypeOfNotification, Modal, setNotification } from "medere-ui-framework";
import LocationEstablishmentFilter from "../components/LocationEstablishmentFilter";
import useEstablishmentFilterSelection from "../../hooks/useEstablishmentFilterSelection";
import CRUDBedModal from "./CRUDBedModal";
import BedTable from "./components/BedTable";

export default function BedView() {
    const dispatch = useDispatch();
    const isMobile = useResizeListener(768);
    const { establishments, orderedEstablishments, setEstablishments, setBedsToEstablishments, setRoomsToEstablishments } = useLocationConfigContext();

    // Camas muestra el filtro también en escritorio.
    const { selectedEstablishmentIds, setSelectedEstablishmentIds } = useEstablishmentFilterSelection(true);

    const [openBedModal, setOpenBedModal] = useState(false);
    const [bedToEdit, setBedToEdit] = useState<Bed | null>(null);
    const [bedToDelete, setBedToDelete] = useState<Bed | null>(null);
    const [openRemoveModal, setOpenRemoveModal] = useState(false);

    const establishmentIdsKey = useMemo(
        () => (establishments ?? []).map((est) => est.buildingid).sort((a, b) => a - b).join(","),
        [establishments]
    );

    useEffect(() => {
        if ((establishments?.length ?? 0) > 0) return;

        let cancelled = false;
        void loadAllEstablishments().then((res) => {
            if (cancelled || !Array.isArray(res)) return;
            setEstablishments(res);
        }).catch(() => {
            if (cancelled) return;
            dispatch(setNotification({
                title: "Ocurrió un error",
                desc: "No se pudieron cargar los establecimientos.",
                icon: "pi pi-times",
                typeOfNotification: ETypeOfNotification.ERROR
            }));
        });

        return () => {
            cancelled = true;
        };
    }, [establishments?.length, setEstablishments, dispatch]);

    useEffect(() => {
        if (!establishmentIdsKey) return;
        void setBedsToEstablishments();
    }, [establishmentIdsKey]);

    const refreshBedsAndRooms = async () => {
        await setBedsToEstablishments();
        await setRoomsToEstablishments();
    };

    const establishmentsToRender = orderedEstablishments.filter((est) =>
        selectedEstablishmentIds.includes(est.buildingid)
    );

    const establishmentsWithBeds = useMemo(
        () =>
            establishmentsToRender.filter((est) => (est.beds ?? []).length > 0),
        [establishmentsToRender]
    );

    const hasBedsToDisplay = establishmentsWithBeds.length > 0;

    const handleOpenCreate = () => {
        setBedToEdit(null);
        setOpenBedModal(true);
    };

    const handleOpenEdit = (bed: Bed) => {
        setBedToEdit(bed);
        setOpenBedModal(true);
    };

    const handleOpenDelete = (bed: Bed) => {
        setBedToDelete(bed);
        setOpenRemoveModal(true);
    };

    const handleDelete = async () => {
        if (!bedToDelete || bedToDelete.bed <= 0) return;
        try {
            await deleteBed(bedToDelete.bed);
            await refreshBedsAndRooms();
            setOpenRemoveModal(false);
            setBedToDelete(null);
            dispatch(setNotification({ title: "Eliminación exitosa", desc: "Se eliminó la cama.", icon: "pi pi-check-circle", typeOfNotification: ETypeOfNotification.INFO }));
        } catch (error: any) {
            const status = error?.response?.status;
            if (status === 409) {
                dispatch(setNotification({ title: "No se puede eliminar", desc: "La cama tiene dependencias asociadas.", icon: "pi pi-exclamation-triangle", typeOfNotification: ETypeOfNotification.WARNING }));
            } else {
                dispatch(setNotification({ title: "Ocurrió un error", desc: "No se pudo eliminar la cama.", icon: "pi pi-times", typeOfNotification: ETypeOfNotification.ERROR }));
            }
        }
    };

    return (
        <div>
            <h1 className="title2">Camas</h1>
            <div className={`emr-border-bottom pb-2 ${isMobile ? "location-mobile-top-toolbar" : "flex flex-row justify-content-between"}`}>
                <p className="grey-color">Las habitaciones se asocian a los sectores de establecimientos creados.</p>
                <Button
                    icon="pi pi-plus-circle"
                    className={`buttonMain ${isMobile ? "location-mobile-create-btn" : "margin-create-button"}`}
                    label="Crear"
                    onClick={handleOpenCreate}
                />
            </div>

            <LocationEstablishmentFilter
                establishments={orderedEstablishments}
                selectedEstablishmentIds={selectedEstablishmentIds}
                onChange={setSelectedEstablishmentIds}
            />

            {establishmentsWithBeds.map((est) => (
                <div key={`bed-est-${est.buildingid}`} className="my-5">
                    <div className="flex flex-row gap-4">
                        <h2 className="title3">{est.buildingname}</h2>
                        <p className="grey-color mt-1">{est.buildingaddress}</p>
                    </div>
                    <BedTable
                        tableId={`locationBedsTable-${est.buildingid}`}
                        beds={est.beds ?? []}
                        onEdit={handleOpenEdit}
                        onDelete={handleOpenDelete}
                    />
                </div>
            ))}

            {!hasBedsToDisplay && (
                <p className="grey-color mt-4">
                    No hay camas cargadas para los establecimientos seleccionados.
                </p>
            )}

            <CRUDBedModal
                visible={openBedModal}
                setVisible={setOpenBedModal}
                establishments={establishments}
                bedToEdit={bedToEdit}
                onSaved={refreshBedsAndRooms}
            />

            <Modal
                className="location-mobile-editor-modal"
                visible={openRemoveModal}
                setVisible={setOpenRemoveModal}
                header={"Estás por eliminar la cama"}
                headerStyle={{ fontSize: "12px", textAlign: "center" }}
                width="w-medium"
                closable={false}
            >
                <div>
                    <p className="grey-color">Esta acción no se puede deshacer.</p>
                    <div style={{ marginTop: "1rem", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                        <Button label="Si, eliminar" onClick={() => { void handleDelete(); }} className="w-full buttonMain" />
                        <Button label={"Cancelar"} onClick={() => setOpenRemoveModal(false)} className="w-full btnCancel2" />
                    </div>
                </div>
            </Modal>
        </div>
    );
}
